import { useState, useEffect, useRef } from 'react';
import {
  Plus, Pencil, Trash2, Upload, Download, Search, X,
  Mail, Phone, UserCircle, Shield, UserCheck, Clock
} from 'lucide-react';
import {
  Button, MessagePlugin, Dialog, Form, Input, Table, Space,
  Popconfirm, Tag
} from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import { eCardApi } from '../../api';
import type { PersonnelDetailVO, PersonnelVO, PersonnelUserVO } from '@/types/eCard';
import ExcelJS from 'exceljs';
import { PreviewableImage } from './components/ImagePreview';

const { FormItem } = Form;

const emptyForm: Partial<PersonnelVO> = {
  address: '',
  avatar: '',
  backgroundImage: '',
  cardEmail: '',
  cardPhone: '',
  company: '',
  contentPosition: 'left',
  displayFields: ['name', 'position', 'avatar', 'phone', 'email', 'company', 'address'],
  email: '',
  fontColor: '#000000',
  name: '',
  nickname: '',
  phone: '',
  position: '',
  status: 1,
  userId: undefined,
  userStatus: 1,
};

const USER_STATUS_MAP: Record<number, { label: string; theme: 'success' | 'danger' | 'default' }> = {
  0: { label: '禁用', theme: 'danger' },
  1: { label: '启用', theme: 'success' },
};

const USER_TYPE_MAP: Record<number, { label: string; theme: 'warning' | 'default' }> = {
  1: { label: '管理员', theme: 'warning' },
  2: { label: '普通用户', theme: 'default' },
};

export default function EmployeeManage() {
  const [list, setList] = useState<PersonnelUserVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PersonnelDetailVO | null>(null);
  const [currentForm, setCurrentForm] = useState<Partial<PersonnelVO>>({ ...emptyForm });
  const formRef = useRef<any>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importStats, setImportStats] = useState({ successCount: 0, failCount: 0 });

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const loadData = async (params?: { pageNum?: number; pageSize?: number }) => {
    setLoading(true);
    try {
      const res = await eCardApi.getPageList({
        pageNum: params?.pageNum || pagination.current,
        pageSize: params?.pageSize || pagination.pageSize,
        name: searchText || undefined,
      });
      if (res.code === 200) {
        setList(res.data?.list || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data?.total || 0,
          ...(params?.pageNum && { current: params.pageNum }),
          ...(params?.pageSize && { pageSize: params.pageSize }),
        }));
      }
    } catch {
      MessagePlugin.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadData({ pageNum: 1 });
  };

  const handleReset = () => {
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadData({ pageNum: 1 });
  };

  const openCreate = () => {
    setEditingRecord(null);
    setCurrentForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = async (row: PersonnelUserVO) => {
    try {
      const res = await eCardApi.getById(row.id!);
      if (res.code === 200 && res.data) {
        const detail = res.data;
        const card = detail.card || {} as any;
        const user = detail.user || {} as any;
        setEditingRecord(detail);
        setCurrentForm({
          ...emptyForm,
          ...card,
          avatar: user.avatar || '',
          cardEmail: card.email || '',
          cardPhone: card.phone || '',
          email: user.email || '',
          name: card.name || '',
          nickname: user.nickname || '',
          phone: user.phone || '',
          username: user.username || '',
          userId: card.userId || user.id,
          userStatus: user.userStatus,
        });
        setShowModal(true);
      } else {
        MessagePlugin.error(res.message || '获取详情失败');
      }
    } catch {
      MessagePlugin.error('获取详情失败');
    }
  };

  const handleSave = async () => {
    if (!currentForm.username?.trim()) { MessagePlugin.warning('请输入用户名'); return; }
    if (!editingRecord && !currentForm.password?.trim()) { MessagePlugin.warning('请输入密码'); return; }
    if (!currentForm.name?.trim()) { MessagePlugin.warning('请输入姓名'); return; }
    if (!currentForm.phone?.trim()) {
      MessagePlugin.warning('请输入手机号'); return;
    } else if (!/^1[3-9]\d{9}$/.test(currentForm.phone.trim())) {
      MessagePlugin.warning('请输入正确的手机号格式'); return;
    }
    if (currentForm.email?.trim() && !/^[\w.-]+@[\w-]+\.[\w.-]+$/.test(currentForm.email.trim())) {
      MessagePlugin.warning('请输入正确的邮箱格式'); return;
    }
    if (currentForm.cardPhone?.trim() && !/^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8})$/.test(currentForm.cardPhone.trim())) {
      MessagePlugin.warning('请输入正确的手机号或座机号格式'); return;
    }
    if (currentForm.cardEmail?.trim() && !/^[\w.-]+@[\w-]+\.[\w.-]+$/.test(currentForm.cardEmail.trim())) {
      MessagePlugin.warning('请输入正确的邮箱格式'); return;
    }

    try {
      const submitData: Partial<PersonnelVO> = {
        ...currentForm,
        address: currentForm.address?.trim() || '',
        avatar: currentForm.avatar?.trim() || '',
        backgroundImage: currentForm.backgroundImage?.trim() || '',
        cardEmail: currentForm.cardEmail?.trim() || '',
        cardPhone: currentForm.cardPhone?.trim() || '',
        company: currentForm.company?.trim() || '',
        contentPosition: currentForm.contentPosition || 'left',
        displayFields: currentForm.displayFields || [],
        email: currentForm.email?.trim() || '',
        fontColor: currentForm.fontColor?.trim() || '',
        name: currentForm.name?.trim() || '',
        nickname: currentForm.nickname?.trim() || '',
        phone: currentForm.phone?.trim() || '',
        position: currentForm.position?.trim() || '',
        status: currentForm.status,
        userId: currentForm.userId,
        userStatus: currentForm.userStatus,
        username: currentForm.username?.trim() || '',
      };
      if (!editingRecord) {
        submitData.password = currentForm.password?.trim() || '';
      } else {
        delete submitData.password;
      }

      const res = editingRecord
        ? await eCardApi.update(submitData)
        : await eCardApi.create(submitData);

      if (res.code === 200) {
        MessagePlugin.success(editingRecord ? '更新成功' : '新增成功');
        setShowModal(false);
        setEditingRecord(null);
        loadData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch {
      MessagePlugin.error('操作失败');
    }
  };

  const handleDelete = async (userId: number) => {
    try {
      const res = await eCardApi.delete(userId);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        loadData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportErrors([]);
    setImportStats({ successCount: 0, failCount: 0 });
    setImporting(false);
  };

  const openImportModal = () => {
    resetImportState();
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    resetImportState();
  };

  const handleImport = async () => {
    if (!importFile) {
      MessagePlugin.warning('请先选择文件');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await eCardApi.import(formData);
      if (res.code === 200) {
        const data = res.data || {};
        const failDetails = data.failDetails || [];
        const errorList: string[] = failDetails.map((f: any) => {
          const rowPart = f.rowIndex !== undefined ? `第${f.rowIndex}行 ` : '';
          const userPart = f.username ? `（${f.username}）` : '';
          const reason = f.reason || '未知错误';
          return `${rowPart}${userPart}${reason}`;
        });
        const successCount = data.successCount || 0;
        const failCount = data.failCount || 0;
        if(failCount > 0){
          setImportErrors(errorList);
          setImportStats({ successCount, failCount });
          setErrorDialogVisible(true);
        } else {
          MessagePlugin.success('导入成功');
        }
        setShowImportModal(false);
        setImportFile(null);
        loadData();
      } else {
        MessagePlugin.error(res.message || '导入失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('租户人员导入模板', { properties: { defaultColWidth: 18 } });

      sheet.columns = [
        { header: '用户名', key: 'username', width: 16 },
        { header: '密码', key: 'password', width: 12 },
        { header: '昵称', key: 'nickname', width: 14 },
        { header: '手机号', key: 'phone', width: 16 },
        { header: '邮箱', key: 'email', width: 24 },
        { header: '头像', key: 'avatar', width: 24 },
        { header: '姓名', key: 'name', width: 12 },
        { header: '名片手机号', key: 'cardPhone', width: 16 },
        { header: '职位', key: 'position', width: 16 },
        { header: '公司', key: 'company', width: 24 },
        { header: '地址', key: 'address', width: 30 },
        { header: '名片邮箱', key: 'cardEmail', width: 24 },
      ];

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FF333333' }, size: 11 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F0FE' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 28;
      headerRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
          left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
          bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
          right: { style: 'thin', color: { argb: 'FFB0BEC5' } },
        };
      });

      // 添加必填列标注
      ['A', 'B', 'G'].forEach((col) => {
        const cell = sheet.getCell(`${col}1`);
        cell.font = { bold: true, color: { argb: 'FFFF0000' }, size: 11 };
      });

      for (let i = 2; i <= 100; i++) {
        const row = sheet.getRow(i);
        row.height = 20;
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
            right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          };
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '租户人员导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      MessagePlugin.success('模板下载成功');
    } catch {
      MessagePlugin.error('模板下载失败');
    }
  };

  const columns: any[] = [
    {
      colKey: 'username',
      title: '用户名',
      width: 140,
      ellipsis: true,
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <span className="text-sm font-medium text-gray-800">{row.username || '-'}</span>
      ),
    },
    {
      colKey: 'nickname',
      title: '昵称',
      width: 120,
      ellipsis: true,
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <span className="text-sm text-gray-600">{row.nickname || '-'}</span>
      ),
    },
    {
      colKey: 'phone',
      title: '手机号',
      width: 150,
      ellipsis: true,
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <span>{row.phone || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'email',
      title: '邮箱',
      ellipsis: true,
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <span className="truncate max-w-[180px]">{row.email || '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'userType',
      title: '用户类型',
      width: 110,
      cell: ({ row }: { row: PersonnelUserVO }) => {
        const info = USER_TYPE_MAP[row.userType ?? 2] || USER_TYPE_MAP[2];
        return <Tag theme={info.theme} size="small" variant="light">{info.label}</Tag>;
      },
    },
    {
      colKey: 'userStatus',
      title: '状态',
      width: 100,
      cell: ({ row }: { row: PersonnelUserVO }) => {
        const info = USER_STATUS_MAP[row.userStatus ?? 1] || USER_STATUS_MAP[1];
        return <Tag theme={info.theme} size="small" variant="light">{info.label}</Tag>;
      },
    },
    {
      colKey: 'createTime',
      title: '创建时间',
      width: 170,
      ellipsis: true,
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span>{row.createTime ? new Date(row.createTime).toLocaleString('zh-CN') : '-'}</span>
        </div>
      ),
    },
    {
      colKey: 'action',
      title: '操作',
      width: 180,
      fixed: 'right',
      cell: ({ row }: { row: PersonnelUserVO }) => (
        <Space>
          <Button theme="primary" variant="text" size="small" icon={<Pencil className="w-4 h-4" />} onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm content="确定删除该人员吗？" onConfirm={() => handleDelete(row.id!)}>
            <Button theme="danger" variant="text" size="small" icon={<Trash2 className="w-4 h-4" />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="flex-1 w-full min-w-0">
      {/* 顶部标题栏 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#1c5bff]" />
          <h1 className="text-lg font-semibold text-gray-900">租户人员管理</h1>
          <span className="text-sm text-gray-400">共 {pagination.total} 人</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" icon={<Upload className="w-4 h-4" />} onClick={openImportModal}>
            批量导入
          </Button>
          <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
            新增人员
          </Button>
        </div>
      </div>

      {/* 搜索栏 */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 whitespace-nowrap">搜索</label>
            <Input
              value={searchText}
              onChange={setSearchText}
              placeholder="姓名 / 用户名 / 手机号"
              clearable
              onClear={() => handleReset()}
              style={{ width: 240 }}
            />
          </div>
          <div className="flex gap-2">
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={handleSearch}>搜索</Button>
            <Button variant="outline" icon={<X className="w-4 h-4" />} onClick={handleReset}>重置</Button>
          </div>
        </div>
      </div> */}

      {/* 数据表格 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <Table
          columns={columns}
          data={list}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showJumper: true,
            showPageSize: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (pageInfo: any) => {
              setPagination((prev) => ({ ...prev, current: pageInfo.current ?? prev.current, pageSize: pageInfo.pageSize ?? prev.pageSize }));
              loadData({ pageNum: pageInfo.current ?? pagination.current, pageSize: pageInfo.pageSize ?? pagination.pageSize });
            },
          }}
          bordered={false}
          hover
          size="medium"
        />
      </div>

      {/* 新增/编辑弹窗 */}
      <Dialog
        header={editingRecord ? '编辑人员' : '新增人员'}
        visible={showModal}
        onClose={() => { setShowModal(false); setEditingRecord(null); }}
        onConfirm={handleSave}
        confirmBtn="保存"
        width={720}
      >
        <Form key={editingRecord ? 'edit' : 'create'} ref={formRef} initialData={currentForm} labelWidth={110}>
          <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-5">
            {/* 账号信息 */}
            <div>
              <div className="text-sm font-medium text-gray-700 pb-2 mb-3 border-b border-gray-200 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4 text-[#1c5bff]" />
                账号信息
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormItem label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
                  <Input value={currentForm.username} onChange={(val) => setCurrentForm({ ...currentForm, username: val })} placeholder="请输入用户名" />
                </FormItem>
                {!editingRecord && (
                  <FormItem label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
                    <Input value={currentForm.password} onChange={(val) => setCurrentForm({ ...currentForm, password: val })} placeholder="请输入密码" type="password" />
                  </FormItem>
                )}
              </div>
            </div>

            {/* 基本信息 */}
            <div>
              <div className="text-sm font-medium text-gray-700 pb-2 mb-3 border-b border-gray-200 flex items-center gap-1.5">
                <UserCircle className="w-4 h-4 text-[#1c5bff]" />
                基本信息
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormItem label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input value={currentForm.name} onChange={(val) => setCurrentForm({ ...currentForm, name: val })} placeholder="请输入姓名" />
                </FormItem>
                <FormItem label="昵称" name="nickname">
                  <Input value={currentForm.nickname} onChange={(val) => setCurrentForm({ ...currentForm, nickname: val })} placeholder="请输入昵称" />
                </FormItem>
                <FormItem label="手机号" name="phone" rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
                ]}>
                  <Input value={currentForm.phone} onChange={(val) => setCurrentForm({ ...currentForm, phone: val })} placeholder="请输入手机号" />
                </FormItem>
                <FormItem label="邮箱" name="email" rules={[
                  { pattern: /^[\w.-]+@[\w-]+\.[\w.-]+$/, message: '请输入正确的邮箱格式' }
                ]}>
                  <Input value={currentForm.email} onChange={(val) => setCurrentForm({ ...currentForm, email: val })} placeholder="请输入邮箱" />
                </FormItem>
                <FormItem label="公司" name="company">
                  <Input value={currentForm.company} onChange={(val) => setCurrentForm({ ...currentForm, company: val })} placeholder="请输入公司" />
                </FormItem>
                <FormItem label="职位" name="position">
                  <Input value={currentForm.position} onChange={(val) => setCurrentForm({ ...currentForm, position: val })} placeholder="请输入职位" />
                </FormItem>
              </div>
              <div className="mt-4">
                <FormItem label="地址" name="address">
                  <Input value={currentForm.address} onChange={(val) => setCurrentForm({ ...currentForm, address: val })} placeholder="请输入地址" />
                </FormItem>
              </div>
            </div>

            {/* 名片信息 */}
            <div>
              <div className="text-sm font-medium text-gray-700 pb-2 mb-3 border-b border-gray-200 flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-[#1c5bff]" />
                名片信息
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormItem label="名片手机号" name="cardPhone" rules={[
                  { pattern: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8})$/, message: '请输入正确的手机号或座机号格式' }
                ]}>
                  <Input value={currentForm.cardPhone} onChange={(val) => setCurrentForm({ ...currentForm, cardPhone: val })} placeholder="名片展示的手机号" />
                </FormItem>
                <FormItem label="名片邮箱" name="cardEmail" rules={[
                  { pattern: /^[\w.-]+@[\w-]+\.[\w.-]+$/, message: '请输入正确的邮箱格式' }
                ]}>
                  <Input value={currentForm.cardEmail} onChange={(val) => setCurrentForm({ ...currentForm, cardEmail: val })} placeholder="名片展示的邮箱" />
                </FormItem>
              </div>
            </div>

            {/* 头像 */}
            <div>
              <div className="text-sm font-medium text-gray-700 pb-2 mb-3 border-b border-gray-200">头像</div>
              <FormItem label="头像" name="avatar">
                <ProductImageUploader
                  value={currentForm.avatar}
                  onChange={(val) => setCurrentForm({ ...currentForm, avatar: val })}
                  simple
                />
              </FormItem>
            </div>
          </div>
        </Form>
      </Dialog>

      {/* 导入弹窗 */}
      <Dialog
        header="批量导入"
        visible={showImportModal}
        onClose={closeImportModal}
        onConfirm={handleImport}
        confirmBtn={importing ? '导入中...' : '确认导入'}
        width={600}
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">下载导入模板</p>
                <p className="text-xs text-gray-500">请先下载模板，按模板格式填写数据后再导入</p>
                <p className="text-xs text-red-500 mt-1">必填列：用户名、密码、姓名（红色标注）</p>
              </div>
              <Button variant="outline" icon={<Download className="w-4 h-4" />} onClick={downloadTemplate}>
                下载模板
              </Button>
            </div>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-3">上传文件</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
                    MessagePlugin.warning('仅支持 XLS、XLSX 格式文件');
                    return;
                  }
                  if (file.size > 10 * 1024 * 1024) {
                    MessagePlugin.warning('文件大小不能超过 10MB');
                    return;
                  }
                  setImportFile(file);
                  MessagePlugin.success(`已选择文件：${file.name}`);
                }
                e.target.value = '';
              }}
              className="hidden"
              id="employee-import-file-input"
            />
            <label
              htmlFor="employee-import-file-input"
              className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-lg hover:border-[#1c5bff] hover:bg-[#1c5bff]/5 transition-colors cursor-pointer"
            >
              {importFile ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-sm text-gray-700">{importFile.name}</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-400 mt-2">点击或拖拽上传文件</span>
                  <span className="text-xs text-gray-400 mt-1">支持 XLS、XLSX 格式，单个不超过 10MB</span>
                </>
              )}
            </label>
          </div>
        </div>
      </Dialog>

      {/* 导入结果弹窗 */}
      <Dialog
        header="导入结果"
        visible={errorDialogVisible}
        onClose={() => {
          setErrorDialogVisible(false);
          setImportErrors([]);
          setImportStats({ successCount: 0, failCount: 0 });
        }}
        confirmBtn="关闭"
        width={700}
      >
        <div className="mb-4">
          <p className="text-gray-700 mb-2">导入完成，但存在以下问题：</p>
          <div className="flex gap-4 text-sm">
            <span className="text-green-600">成功：{importStats.successCount} 条</span>
            <span className="text-red-600">失败：{importStats.failCount} 条</span>
          </div>
          {importStats.successCount > 0 && (
            <div className="mt-2">
              <span className="text-sm text-red-600">
                注意：由于有成功数据在里面，请将成功数据删掉，并参考失败原因修改失败数据的格式，再重新上传，以免造成数据重复！！！
              </span>
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
          <ul className="space-y-2">
            {importErrors.map((error, index) => (
              <li key={index} className="text-sm text-red-600">
                {error}
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </div>
  );
}
